import { IComponentLoader } from "../../interfaces/IComponentLoader";
import { ComponentAdapterParent } from "./component-adapter-parent";
import { SkillNameList } from "../../enum/enumSkillNameList";

import { SmartComponentPropertyDetails } from "../smart-components/smart-component-property-details";
import { AddressDetails } from "../smart-components/smart-component-property";

/**
 * This class handle properties of thermostat
 */
export class ComponentAdapterThermostat
  extends ComponentAdapterParent
  implements IComponentLoader {
  private _actualTemperature: AddressDetails = null;
  private _heatingCoolingCommand: AddressDetails = null;
  private _heatingCoolingState: AddressDetails = null;
  private _valveState: AddressDetails = null;
  private _hvacCommand: AddressDetails = null;
  private _hvacState: AddressDetails = null;
  private _comfortSwitch: AddressDetails = null;
  private _standbySwitch: AddressDetails = null;
  private _economySwitch: AddressDetails = null;
  private _protectSwitch: AddressDetails = null;
  private _setpointBase: AddressDetails = null;

  private _coolingComfort: AddressDetails = null;
  private _coolingStandby: AddressDetails = null;
  private _coolingEconomy: AddressDetails = null;
  private _coolingProtection: AddressDetails = null;
  private _heatingComfort: AddressDetails = null;
  private _heatingStandby: AddressDetails = null;
  private _heatingEconomy: AddressDetails = null;
  private _heatingProtection: AddressDetails = null;

  private _windowsOpen: AddressDetails = null;
  private _fancoilCommand: AddressDetails = null;
  private _fancoilCommandMin: string = "";
  private _fancoilCommandMax: string = "";
  private _fancoilState: AddressDetails = null;
  private _fancoilStateMin: string = "";
  private _fancoilStateMax: string = "";
  private _fancoilV0: AddressDetails = null;
  private _fancoilV1: AddressDetails = null;
  private _fancoilV2: AddressDetails = null;
  private _fancoilV3: AddressDetails = null;
  private _autoManCommandFancoil: AddressDetails = null;
  private _autoManStateFancoil: AddressDetails = null;

  private _setpointBaseMin: string = "";
  private _setpointBaseMax: string = "";
  private _setpointHeatingComfortMin: string = "";
  private _setpointHeatingComfortMax: string = "";
  private _setpointHeatingStandbyMin: string = "";
  private _setpointHeatingStandbyMax: string = "";
  private _setpointHeatingEconomyMin: string = "";
  private _setpointHeatingEconomyMax: string = "";
  private _setpointHeatingProtectionMin: string = "";
  private _setpointHeatingProtectionMax: string = "";
  private _setpointCoolingComfortMin: string = "";
  private _setpointCoolingComfortMax: string = "";
  private _setpointCoolingStandbyMin: string = "";
  private _setpointCoolingStandbyMax: string = "";
  private _setpointCoolingEconomyMin: string = "";
  private _setpointCoolingEconomyMax: string = "";
  private _setpointCoolingProtectionMin: string = "";
  private _setpointCoolingProtectionMax: string = "";
  private _actualSetpointOrSummerSetpoint: AddressDetails = null;
  private _actualWinterSetpoint: AddressDetails = null;
  private _shiftSetpoint: AddressDetails = null;
  private _shiftSetpointMin: string = "";
  private _shiftSetpointMax: string = "";

  private _hasUniqueSetpointForSeason: boolean = false;

  /**
   * Get a flag that checks whether the thermostat has a unique setpoint
   */
  get HasUniqueSetpointForSeason(): boolean {
    return this._hasUniqueSetpointForSeason;
  }

  /**
   * Get actual temperature address
   */
  get ActualTemperature(): AddressDetails {
    return this._actualTemperature;
  }

  /**
   * Get heating cooling command address
   */
  get HeatingCoolingCommand(): AddressDetails {
    return this._heatingCoolingCommand;
  }

  /**
   * Get heating cooling state address
   */
  get HeatingCoolingState(): AddressDetails {
    return this._heatingCoolingState;
  }

  /**
   * Get valve state address
   */
  get ValveState(): AddressDetails {
    return this._valveState;
  }

  /**
   * Get hvac command address
   */
  get HvacCommand(): AddressDetails {
    return this._hvacCommand;
  }

  /**
   * Get hvac state address
   */
  get HvacState(): AddressDetails {
    return this._hvacState;
  }

  /**
   * Get comfort switch address
   */
  get ComfortSwitch(): AddressDetails {
    return this._comfortSwitch;
  }

  /**
   * Get standby switch address
   */
  get StandbySwitch(): AddressDetails {
    return this._standbySwitch;
  }

  /**
   * Get economy switch address
   */
  get EconomySwitch(): AddressDetails {
    return this._economySwitch;
  }

  /**
   * Get protect switch address
   */
  get ProtectSwitch(): AddressDetails {
    return this._protectSwitch;
  }

  /**
   * Get setpoint base address
   */
  get SetpointBase(): AddressDetails {
    return this._setpointBase;
  }

  /**
   * Get cooling comfort address
   */
  get CoolingComfort(): AddressDetails {
    return this._coolingComfort;
  }

  /**
   * Get cooling standby address
   */
  get CoolingStandby(): AddressDetails {
    return this._coolingStandby;
  }

  /**
   * Get cooling economy address
   */
  get CoolingEconomy(): AddressDetails {
    return this._coolingEconomy;
  }

  /**
   * Get cooling protection address
   */
  get CoolingProtection(): AddressDetails {
    return this._coolingProtection;
  }

  /**
   * Get heating comfort address
   */
  get HeatingComfort(): AddressDetails {
    return this._heatingComfort;
  }

  /**
   * Get heating standby address
   */
  get HeatingStandby(): AddressDetails {
    return this._heatingStandby;
  }

  /**
   * Get heating economy address
   */
  get HeatingEconomy(): AddressDetails {
    return this._heatingEconomy;
  }

  /**
   * Get heating protection address
   */
  get HeatingProtection(): AddressDetails {
    return this._heatingProtection;
  }

  /**
   * Get windows open address
   */
  get WindowsOpen(): AddressDetails {
    return this._windowsOpen;
  }

  /**
   * Get fancoil command address
   */
  get FancoilCommand(): AddressDetails {
    return this._fancoilCommand;
  }

  /**
   * Get fancoil command min
   */
  get FancoilCommandMin(): string {
    return this._fancoilCommandMin;
  }

  /**
   * Get fancoil command max
   */
  get FancoilCommandMax(): string {
    return this._fancoilCommandMax;
  }

  /**
   * Get fancoil state address
   */
  get FancoilState(): AddressDetails {
    return this._fancoilState;
  }

  /**
   * Get fancoil state min
   */
  get FancoilStateMin(): string {
    return this._fancoilStateMin;
  }

  /**
   * Get fancoil state max
   */
  get FancoilStateMax(): string {
    return this._fancoilStateMax;
  }

  /**
   * Get fancoilV0 command address
   */
  get FancoilV0(): AddressDetails {
    return this._fancoilV0;
  }

  /**
   * Get fancoilV1 command address
   */
  get FancoilV1(): AddressDetails {
    return this._fancoilV1;
  }

  /**
   * Get fancoilV2 command address
   */
  get FancoilV2(): AddressDetails {
    return this._fancoilV2;
  }

  /**
   * Get fancoilV3 command address
   */
  get FancoilV3(): AddressDetails {
    return this._fancoilV3;
  }

  /**
   * Get auto/man fancoil address
   */
  get AutoManCommandFancoil(): AddressDetails {
    return this._autoManCommandFancoil;
  }

  /**
   * Get state auto/man fancoil address
   */
  get AutoManStateFancoil(): AddressDetails {
    return this._autoManStateFancoil;
  }

  /**
   * Get setpoint base min
   */
  get SetpointBaseMin(): string {
    return this._setpointBaseMin;
  }

  /**
   * Get setpoint base max
   */
  get SetpointBaseMax(): string {
    return this._setpointBaseMax;
  }

  /**
   * Get setpoint heating comfort min
   */
  get SetpointHeatingComfortMin(): string {
    return this._setpointHeatingComfortMin;
  }

  /**
   * Get setpoint heating comfort max
   */
  get SetpointHeatingComfortMax(): string {
    return this._setpointHeatingComfortMax;
  }

  /**
   * Get setpoint heating standby min
   */
  get SetpointHeatingStandbyMin(): string {
    return this._setpointHeatingStandbyMin;
  }

  /**
   * Get setpoint heating standby max
   */
  get SetpointHeatingStandbyMax(): string {
    return this._setpointHeatingStandbyMax;
  }

  /**
   * Get setpoint heating economy min
   */
  get SetpointHeatingEconomyMin(): string {
    return this._setpointHeatingEconomyMin;
  }

  /**
   * Get setpoint heating economy max
   */
  get SetpointHeatingEconomyMax(): string {
    return this._setpointHeatingEconomyMax;
  }

  /**
   * Get setpoint heating protection min
   */
  get SetpointHeatingProtectionMin(): string {
    return this._setpointHeatingProtectionMin;
  }

  /**
   * Get setpoint heating protection max
   */
  get SetpointHeatingProtectionMax(): string {
    return this._setpointHeatingProtectionMax;
  }

  /**
   * Get setpoint cooling comfort min
   */
  get SetpointCoolingComfortMin(): string {
    return this._setpointCoolingComfortMin;
  }

  /**
   * Get setpoint cooling comfort max
   */
  get SetpointCoolingComfortMax(): string {
    return this._setpointCoolingComfortMax;
  }

  /**
   * Get setpoint cooling standby min
   */
  get SetpointCoolingStandbyMin(): string {
    return this._setpointCoolingStandbyMin;
  }

  /**
   * Get setpoint cooling standby max
   */
  get SetpointCoolingStandbyMax(): string {
    return this._setpointCoolingStandbyMax;
  }

  /**
   * Get setpoint cooling economy min
   */
  get SetpointCoolingEconomyMin(): string {
    return this._setpointCoolingEconomyMin;
  }

  /**
   * Get setpoint cooling economy max
   */
  get SetpointCoolingEconomyMax(): string {
    return this._setpointCoolingEconomyMax;
  }

  /**
   * Get setpoint cooling protection min
   */
  get SetpointCoolingProtectionMin(): string {
    return this._setpointCoolingProtectionMin;
  }

  /**
   * Get setpoint cooling protection max
   */
  get SetpointCoolingProtectionMax(): string {
    return this._setpointCoolingProtectionMax;
  }

  /**
   * Get actual setpoint/summer setpoint address
   */
  get ActualSetpointOrSummerSetpoint(): AddressDetails {
    return this._actualSetpointOrSummerSetpoint;
  }

  /**
 * Get actual winter setpoint address
 */
  get ActualWinterSetpoint(): AddressDetails {
    return this._actualWinterSetpoint;
  }

  /**
   * Get shift setpoint address
   */
  get ShiftSetpoint(): AddressDetails {
    return this._shiftSetpoint;
  }

  /**
   * Get shift setpoint min
   */
  get ShiftSetpointMin(): string {
    return this._shiftSetpointMin;
  }

  /**
   * Get shift setpoint max
   */
  get ShiftSetpointMax(): string {
    return this._shiftSetpointMax;
  }

  constructor() {
    super();
  }

  public fillProperty(
    listSmartComponentSkills: Array<SmartComponentPropertyDetails>
  ): void {
    this._name =
      listSmartComponentSkills.find(
        (titleSkill) => titleSkill.titleSkill === SkillNameList.THERMOSTAT_NAME
      )?.value || "";
    this._description =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_DESCRIPTION
      )?.value || "";
    this._actualTemperature =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_ACTUAL
      )?.address || null;
    this._heatingCoolingCommand =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_HEATING_COOLING_COMMAND
      )?.address || null;
    this._heatingCoolingState =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_HEATING_COOLING_STATE
      )?.address || null;
    this._valveState = listSmartComponentSkills.find(
      (titleSkill) =>
        titleSkill.titleSkill === SkillNameList.THERMOSTAT_VALVE_STATE
    )?.address;
    this._hvacCommand =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_HVACMODE_COMMAND
      )?.address || null;
    this._hvacState =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_HVACMODE_STATE
      )?.address || null;
    this._comfortSwitch =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_COMFORT_SWITCH
      )?.address || null;
    this._standbySwitch =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_STANDBY_SWITCH
      )?.address || null;
    this._economySwitch =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_ECONOMY_SWITCH
      )?.address || null;
    this._protectSwitch =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_PROTECTION_SWITCH
      )?.address || null;
    this._setpointBase =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SETPOINT_BASE
      )?.address || null;

    this._coolingComfort =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_COMFORT
      )?.address || null;
    this._coolingStandby =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_STANDBY
      )?.address || null;
    this._coolingEconomy =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_ECONOMY
      )?.address || null;
    this._coolingProtection =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_PROTECTION
      )?.address || null;
    this._heatingComfort =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_COMFORT
      )?.address || null;
    this._heatingStandby =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_STANDBY
      )?.address || null;
    this._heatingEconomy =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_ECONOMY
      )?.address || null;
    this._heatingProtection =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_PROTECTION
      )?.address || null;
    this._windowsOpen =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_WINDOWS_OPEN_STATE
      )?.address || null;

    this._fancoilCommand =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_COMMAND
      )?.address || null;
    this._fancoilCommandMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_COMMAND_MIN
      )?.value || "";
    this._fancoilCommandMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_COMMAND_MAX
      )?.value || "";
    this._fancoilState =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_STATE
      )?.address || null;
    this._fancoilStateMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_STATE_MIN
      )?.value || "";
    this._fancoilStateMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_STATE_MAX
      )?.value || "";
    this._fancoilV0 =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_V0
      )?.address || null;
    this._fancoilV1 =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_V1
      )?.address || null;
    this._fancoilV2 =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_V2
      )?.address || null;
    this._fancoilV3 =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_FANCOIL_V3
      )?.address || null;
    this._autoManCommandFancoil =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_FANCOIL_AUTO_MAN_COMMAND
      )?.address || null;
    this._autoManStateFancoil =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_AUTO_MAN_STATE
      )?.address || null;

    this._setpointBaseMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SETPOINT_BASE_MIN
      )?.value || "";
    this._setpointBaseMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SETPOINT_BASE_MAX
      )?.value || "";
    this._setpointHeatingComfortMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_COMFORT_MIN
      )?.value || "";
    this._setpointHeatingComfortMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_COMFORT_MAX
      )?.value || "";

    this._setpointHeatingStandbyMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_STANDBY_MIN
      )?.value || "";

    this._setpointHeatingStandbyMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_STANDBY_MAX
      )?.value || "";

    this._setpointHeatingEconomyMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_ECONOMY_MIN
      )?.value || "";

    this._setpointHeatingEconomyMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_ECONOMY_MAX
      )?.value || "";

    this._setpointHeatingProtectionMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_PROTECTION_MIN
      )?.value || "";

    this._setpointHeatingProtectionMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_HEATING_PROTECTION_MAX
      )?.value || "";

    this._setpointCoolingComfortMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_COMFORT_MIN
      )?.value || "";

    this._setpointCoolingComfortMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_COMFORT_MAX
      )?.value || "";

    this._setpointCoolingStandbyMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_STANDBY_MIN
      )?.value || "";

    this._setpointCoolingStandbyMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_STANDBY_MAX
      )?.value || "";

    this._setpointCoolingEconomyMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_ECONOMY_MIN
      )?.value || "";

    this._setpointCoolingEconomyMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_ECONOMY_MAX
      )?.value || "";

    this._setpointCoolingProtectionMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_PROTECTION_MIN
      )?.value || "";

    this._setpointCoolingProtectionMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill ===
          SkillNameList.THERMOSTAT_SETPOINT_COOLING_PROTECTION_MAX
      )?.value || "";
    this._actualSetpointOrSummerSetpoint =
      listSmartComponentSkills.find(
        (titleSkill) => titleSkill.titleSkill === SkillNameList.THERMOSTAT_ACTUAL_SETPOINT_SUMMER
      )?.address || null;
    this._actualWinterSetpoint =
      listSmartComponentSkills.find(
        (titleSkill) => titleSkill.titleSkill === SkillNameList.THERMOSTAT_ACTUAL_SETPOINT_WINTER
      )?.address || null;

    this._shiftSetpoint =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SHIFT_SETPOINT
      )?.address || null;
    this._shiftSetpointMin =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SHIFT_SETPOINT_MIN
      )?.value || "";
    this._shiftSetpointMax =
      listSmartComponentSkills.find(
        (titleSkill) =>
          titleSkill.titleSkill === SkillNameList.THERMOSTAT_SHIFT_SETPOINT_MAX
      )?.value || "";

    if (this._hvacCommand && this._heatingCoolingCommand == null) {
      if (
        this._heatingComfort &&
        this._coolingComfort == null &&
        this._heatingStandby &&
        this._coolingStandby == null &&
        this._heatingEconomy &&
        this._coolingEconomy == null &&
        this._heatingProtection &&
        this._coolingProtection == null
      ) {
        this._hasUniqueSetpointForSeason = true;
      }
    }

    this.fillMissingProperty();
    this.fillAddressState();
  }

  public fillMissingProperty(): void {
    if (
      this._heatingCoolingState === null &&
      this._heatingCoolingCommand !== null
    ) {
      this._heatingCoolingState = this._heatingCoolingCommand;
    }

    if (this._hvacState === null && this._hvacCommand !== null) {
      this._hvacState = this._hvacCommand;
    }

    if (this._comfortSwitch === null && this._standbySwitch !== null) {
      this._comfortSwitch = this._standbySwitch;
    }

    if (this._standbySwitch === null && this._comfortSwitch !== null) {
      this._standbySwitch = this._comfortSwitch;
    }

    if (this._fancoilState === null && this._fancoilCommand !== null) {
      this._fancoilState = this._fancoilCommand;
    }

    if (this._autoManStateFancoil === null && this._autoManCommandFancoil !== null) {
      this._autoManStateFancoil = this._autoManCommandFancoil;
    }
  }

  public fillAddressState(): void {
    if (this._actualTemperature) {
      this._listSubscribeAddress.push(this._actualTemperature);
    }

    if (this._heatingCoolingState) {
      this._listSubscribeAddress.push(this._heatingCoolingState);
    }

    if (this._valveState) {
      this._listSubscribeAddress.push(this._valveState);
    }

    if (this._hvacState) {
      this._listSubscribeAddress.push(this._hvacState);
    }

    if (this._comfortSwitch) {
      this._listSubscribeAddress.push(this._comfortSwitch);
    }

    if (this._standbySwitch) {
      this._listSubscribeAddress.push(this._standbySwitch);
    }

    if (this._economySwitch) {
      this._listSubscribeAddress.push(this._economySwitch);
    }

    if (this._protectSwitch) {
      this._listSubscribeAddress.push(this._protectSwitch);
    }

    if (this._setpointBase) {
      this._listSubscribeAddress.push(this._setpointBase);
    }

    if (this._coolingComfort) {
      this._listSubscribeAddress.push(this._coolingComfort);
    }

    if (this._coolingStandby) {
      this._listSubscribeAddress.push(this._coolingStandby);
    }

    if (this._coolingEconomy) {
      this._listSubscribeAddress.push(this._coolingEconomy);
    }

    if (this._coolingProtection) {
      this._listSubscribeAddress.push(this._coolingProtection);
    }

    if (this._heatingComfort) {
      this._listSubscribeAddress.push(this._heatingComfort);
    }

    if (this._heatingStandby) {
      this._listSubscribeAddress.push(this._heatingStandby);
    }

    if (this._heatingEconomy) {
      this._listSubscribeAddress.push(this._heatingEconomy);
    }

    if (this._heatingProtection) {
      this._listSubscribeAddress.push(this._heatingProtection);
    }

    if (this._windowsOpen) {
      this._listSubscribeAddress.push(this._windowsOpen);
    }

    if (this._fancoilState) {
      this._listSubscribeAddress.push(this._fancoilState);
    }

    if (this._fancoilV0) {
      this._listSubscribeAddress.push(this._fancoilV0);
    }

    if (this._fancoilV1) {
      this._listSubscribeAddress.push(this._fancoilV1);
    }

    if (this._fancoilV2) {
      this._listSubscribeAddress.push(this._fancoilV2);
    }

    if (this._fancoilV3) {
      this._listSubscribeAddress.push(this._fancoilV3);
    }

    if (this._autoManStateFancoil) {
      this._listSubscribeAddress.push(this._autoManStateFancoil);
    }

    if (this._actualSetpointOrSummerSetpoint) {
      this._listSubscribeAddress.push(this._actualSetpointOrSummerSetpoint);
    }

    if (this._actualWinterSetpoint) {
      this._listSubscribeAddress.push(this._actualWinterSetpoint);
    }

    if (this._shiftSetpoint) {
      this._listSubscribeAddress.push(this._shiftSetpoint);
    }
  }
}
